/**
 * Fair Price Overlay Primitive
 *
 * Renders the JJ Simon "Fair Price" indicator entirely on canvas as a series
 * primitive (mirrors BosLabelPrimitive's structure: primitive + pane view +
 * renderer). A canvas primitive paints at coordinates derived from the
 * EXISTING time scale without contributing any data points, so the shared
 * time scale (and load-time snap-to-live-edge) is untouched.
 *
 * Draws:
 * - Body-box anchors ("rth929" 9:29 body, "prenews" 8:29 body, "migrated"
 *   migration box): a translucent horizontal band (priceLow..priceHigh) from
 *   the anchor's set time to the right edge of the pane, with 1px top/bottom
 *   borders and a small label ("9:29" / "NEWS" / "FAIR PRICE ->") at the right edge.
 * - Open anchors ("open6pm"/"open8pm"/"open2pm"): a 1px dashed horizontal
 *   line from the set time to the right edge with a "GLBX"/"ASIA"/"PM" session label.
 * - The ACTIVE anchor (kind === activeAnchorKind) renders bright: solid 2px
 *   line / band border at full opacity with a subtle glow, plus a price label
 *   chip at the right edge (e.g. "8PM 28830.00").
 * - Distance guide: when armed, a thin dotted vertical connector from the
 *   latest bar's close to the active anchor price with the distance text
 *   midway ("81.5pt"); skipped when the two are < 8px apart.
 * - Inactive anchors are dimmed to 40% opacity.
 * - Signal badges, unambiguously anchored to their bar: a filled triangle
 *   touching the candle (apex at the low pointing up for longs, at the high
 *   pointing down for shorts), a 1px stem from the triangle to a two-line
 *   info chip offset ~32px from the candle, and a tiny dot at the exact entry
 *   price. Chip line 1 is "A+ ▼ 28862.00" (grade + arrow + entry), line 2 is
 *   "50/76½ · 8PM · 81pt" (bracket + anchor + distance-when-present). Chips
 *   from nearby bars stack vertically (occupied-rect tracking per frame) so
 *   they never overlap; chips whose bar is off the visible range are skipped.
 * - Signal lifecycle treatment (status optional; missing = live):
 *   live    -> full-opacity chip + a small bright dot in the chip's top-right
 *              corner. When a NEW live signal arrives, the dot flashes via a
 *              short finite rAF pulse (BosLabelPrimitive pattern) then settles
 *              to a steady glow — no perpetual animation loop.
 *   win     -> chip dimmed to 55%, green ✓ glyph prefixed to line 1, green border.
 *   stop    -> chip dimmed to 55%, red ✗ glyph prefixed to line 1, red border.
 *   expired -> chip dimmed to 40%, grey border, "exp" appended to line 2.
 *
 * Canvas hygiene: every draw is wrapped in save/restore with shadows and
 * dashes reset; the renderer never throws (a bad frame is dropped, not fatal).
 */

import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import {
  LAYOUT_PRIORITY,
  MarkerLayoutClient,
  type LayoutBox,
  type LayoutContext,
  type LayoutItem,
  type MarkerLayoutRegistry,
} from './markerLayout';

// ============================================================================
// Input types (fed by ChartView from fairPriceStore)
// ============================================================================

export interface FairPriceAnchorItem {
  kind: 'rth929' | 'open6pm' | 'open8pm' | 'open2pm' | 'prenews' | 'migrated';
  /** Anchor reference price (line y for open anchors). */
  price: number;
  /** Top of the body box (band anchors). */
  priceHigh: number;
  /** Bottom of the body box (band anchors). */
  priceLow: number;
  /** Anchor set time in SECONDS, aligned to a bar boundary by the caller. */
  setAtTime: number;
  active: boolean;
}

export type FairPriceSignalItemStatus = 'live' | 'win' | 'stop' | 'expired';

export interface FairPriceSignalItem {
  /** Stable signal id (used to detect newly-arrived live signals for the pulse). */
  id?: string;
  /** Signal bar time in SECONDS, aligned to a bar boundary by the caller. */
  time: number;
  /** Entry price — marked with a tiny dot on the signal bar. */
  price: number;
  grade: 'A+' | 'A';
  direction: 'long' | 'short';
  /** Bracket text, e.g. "25/38" or "50/76½" (½ appended by the caller when halfSize). */
  riskText: string;
  /** Anchor the signal fired against (short name on chip line 2). */
  anchorKind: FairPriceAnchorItem['kind'];
  /** Points from anchor at signal time; omitted by older engine builds — rendered only when present. */
  distance?: number;
  /** Lifecycle status; omitted by older engine builds — treated as 'live'. */
  status?: FairPriceSignalItemStatus;
}

export interface FairPriceData {
  anchors: FairPriceAnchorItem[];
  signals: FairPriceSignalItem[];
  /** Kind of the currently-active anchor (bright render + distance guide target), null when none. */
  activeAnchorKind: string | null;
  /** Armed (distance gate open) — enables the distance guide connector. */
  armed: boolean;
}

// ============================================================================
// Styling
// ============================================================================

interface AnchorStyle {
  label: string;
  color: string; // base hex color
  band: boolean; // band (body box) vs dashed open line
}

/**
 * Where a label was drawn, in CSS pixels.
 *
 * Recorded at draw time because the renderer is the only thing that knows the
 * final geometry — text width depends on the measured font, and the label sits
 * relative to whichever of the band edges ended up on top. Recomputing it for
 * hit-testing would be a second implementation of the same layout, free to
 * drift from the one that actually drew.
 */
export interface FairPriceLabelRect {
  kind: FairPriceAnchorItem['kind'];
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Topmost label containing the point, or null.
 *
 * Pure and exported so the geometry can be tested without a canvas, matching
 * `hitTestMarkers` in TradeMarkerPrimitive. Searches backwards so the last one
 * drawn — the one painted on top where labels overlap — wins the hover.
 */
export function hitTestLabels(
  rects: readonly FairPriceLabelRect[],
  px: number,
  py: number
): FairPriceLabelRect | null {
  for (let i = rects.length - 1; i >= 0; i--) {
    const r = rects[i];
    if (px >= r.left && px <= r.right && py >= r.top && py <= r.bottom) return r;
  }
  return null;
}

const ANCHOR_STYLES: Record<FairPriceAnchorItem['kind'], AnchorStyle> = {
  rth929: { label: '9:29', color: '#ffd700', band: true },
  prenews: { label: 'NEWS', color: '#ff9800', band: true },
  // Named for what it tells the trader - fair price has moved HERE - rather than
  // for the internal mechanism ("migration"). The arrow points at the time and
  // price that follow it: "FAIR PRICE -> 9:40 AM 29471.00".
  migrated: { label: 'FAIR PRICE →', color: '#9575cd', band: true }, // violet-ish migration box
  // Session names, not clock times: the sessions are defined in ET (6pm/8pm/2pm ET)
  // but the chart renders in the user's local tz, so clock labels would mislead.
  open6pm: { label: 'GLBX', color: '#64b5f6', band: false },
  open8pm: { label: 'ASIA', color: '#ba68c8', band: false },
  open2pm: { label: 'PM', color: '#4db6ac', band: false },
};

/**
 * Label for an anchor.
 *
 * Body-box anchors are pinned to a specific candle, so they show that candle's
 * clock time — in the VIEWER's timezone, never ET. The engine defines the RTH
 * body as the 9:29 ET candle, but on a PDT screen that bar is 6:29, and
 * printing "9:29" next to a 6:29 bar is just wrong. Session-open anchors keep
 * their names (GLBX / ASIA / PM) since a name travels across timezones cleanly.
 */
function anchorLabel(anchor: FairPriceAnchorItem, style: AnchorStyle): string {
  if (!style.band) return style.label;
  // setAtTime is bar-aligned SECONDS (see FairPriceAnchorItem).
  const ms = anchor.setAtTime * 1000;
  if (!Number.isFinite(ms) || ms <= 0) return style.label;
  try {
    const clock = new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    // "NEWS 5:29" / "FAIR PRICE -> 7:04" keep their tag; the RTH body is just its time.
    return style.label === '9:29' ? clock : `${style.label} ${clock}`;
  } catch {
    return style.label;
  }
}

const BAND_FILL_ALPHA = 0.1;
const BAND_BORDER_ALPHA = 0.55;
const INACTIVE_ALPHA = 0.4; // inactive anchors dimmed to 40%
const SIGNAL_LONG_COLOR = '#00c853';
const SIGNAL_SHORT_COLOR = '#ef5350';
const ACTIVE_GLOW_BLUR = 6; // subtle glow on the active anchor stroke (logical px)
const GUIDE_MIN_GAP_PX = 8; // skip the distance guide when close/anchor are nearer than this

/**
 * Nudge the distance guide clear of the candle it measures from.
 *
 * The guide anchors on the live candle's close, so drawing it at that candle's
 * own x put the dotted line straight down the wick — two thin vertical marks
 * overlapping, which reads as one smudged line rather than two things. Offset
 * to the RIGHT because the live candle is the rightmost bar: there is empty
 * pane between it and the price axis, whereas going left would lay the guide
 * over the previous candle instead and solve nothing.
 */
const GUIDE_X_OFFSET_PX = 6;
const SIGNAL_CHIP_OFFSET_PX = 32; // gap between the candle edge and the info chip (logical px)
const SIGNAL_STACK_GAP_PX = 4; // vertical gap between stacked chips (logical px)
const SIGNAL_TRIANGLE_W_PX = 7; // candle-anchoring triangle width (logical px)
const SIGNAL_TRIANGLE_H_PX = 5; // candle-anchoring triangle height (logical px)

// --- Signal lifecycle treatment ---
const STATUS_WIN_COLOR = '#00c853'; // ✓ glyph + border for wins
const STATUS_STOP_COLOR = '#ef5350'; // ✗ glyph + border for stops
const STATUS_EXPIRED_COLOR = '#78909c'; // grey border for expired
const RESOLVED_ALPHA = 0.55; // win/stop chips dimmed to ~55%
const EXPIRED_ALPHA = 0.4; // expired chips dimmed to ~40%
// Live indicator: a small bright dot in the chip's top-right corner. Amber is
// deliberately neither the long-green nor the short-red so it reads as
// "tracking", not an outcome. A NEW live signal kicks a short finite rAF pulse
// (same pattern as BosLabelPrimitive) that briefly boosts the dot's glow/size.
const LIVE_DOT_COLOR = '#ffd54f';
const LIVE_DOT_RADIUS_PX = 2.5; // steady dot radius (logical px)
const LIVE_DOT_GLOW_BASE_PX = 2; // steady glow blur (logical px)
const LIVE_DOT_GLOW_PEAK_EXTRA_PX = 5; // extra glow at pulse peak (logical px)
const LIVE_DOT_PULSE_EXTRA_RADIUS_PX = 1.5; // extra radius at pulse peak (logical px)
const LIVE_PULSE_DURATION_MS = 900; // finite pulse length; loop stops after this

/** Chip line-2 anchor short names (match FairPriceStatusChip's naming). */
const SIGNAL_ANCHOR_LABELS: Record<FairPriceAnchorItem['kind'], string> = {
  rth929: '929',
  prenews: 'NEWS',
  migrated: 'FAIR PRICE →',
  open6pm: 'GLBX',
  open8pm: 'ASIA',
  open2pm: 'PM',
};

/** hex "#rrggbb" -> "rgba(r,g,b,a)". Falls back to the input on parse failure. */
function hexToRgba(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ============================================================================
// Renderer item types (logical px coordinates, precomputed per frame)
// ============================================================================

interface AnchorRendererItem {
  /** Carried through so a drawn label can be traced back to its anchor for
   *  hover. The renderer is otherwise geometry-only. */
  kind: FairPriceAnchorItem['kind'];
  band: boolean;
  startX: number; // left edge of the band/line (logical px)
  rightX: number; // right edge of the pane (logical px)
  yTop: number; // band top (== yPrice for line anchors)
  yBottom: number; // band bottom (== yPrice for line anchors)
  yPrice: number; // reference price y (line y / label anchor)
  color: string;
  label: string;
  alpha: number; // 1.0 active, INACTIVE_ALPHA otherwise
  /** true for the anchor whose kind === activeAnchorKind: 2px solid + glow + price chip. */
  emphasized: boolean;
  /** Formatted anchor price for the emphasized price chip (e.g. "28830.00"). */
  priceText: string;
}

interface GuideRendererItem {
  x: number; // latest bar center (logical px)
  yClose: number; // latest close coordinate (logical px)
  yAnchor: number; // active anchor price coordinate (logical px)
  text: string; // e.g. "81.5pt"
  color: string; // active anchor color
}

interface SignalRendererItem {
  x: number; // bar center (logical px)
  yEntry: number; // exact entry price coordinate (logical px) — tiny dot marker
  yCandleEdge: number; // candle low (long) / high (short) coordinate (logical px)
  below: boolean; // chip below the bar (long) vs above (short)
  line1: string; // grade + direction arrow + entry, e.g. "A+ ▼ 28862.00" (glyph NOT included)
  line2: string; // bracket · anchor · distance, e.g. "50/76½ · 8PM · 81pt" ("· exp" appended when expired)
  color: string;
  // --- Lifecycle treatment ---
  alpha: number; // 1 live, RESOLVED_ALPHA win/stop, EXPIRED_ALPHA expired
  borderColor: string; // chip border: direction color live, green/red/grey resolved
  glyph: string; // '' | '✓' | '✗' — drawn before line1 in glyphColor
  glyphColor: string;
  liveDot: boolean; // draw the bright live dot in the chip's top-right corner
  /** Shared layout: the chip's box (CSS px), or null when folded into a "+N" chip. Absent = stack alone. */
  chipBox?: LayoutBox | null;
  /** Shared layout: y of the triangle's apex, or null when folded. Absent = the candle edge. */
  triApex?: number | null;
}

/** The two chip lines and lifecycle glyph of a signal (layout and draw share them). */
function signalLines(s: FairPriceSignalItem): { line1: string; line2: string; glyph: string; isLong: boolean } {
  const isLong = s.direction === 'long';
  const line2Parts = [s.riskText, SIGNAL_ANCHOR_LABELS[s.anchorKind]].filter(Boolean);
  if (typeof s.distance === 'number' && Number.isFinite(s.distance)) line2Parts.push(`${Math.round(s.distance)}pt`);
  const status: FairPriceSignalItemStatus = s.status ?? 'live';
  if (status === 'expired') line2Parts.push('exp');
  return {
    line1: `${s.grade} ${isLong ? '▲' : '▼'} ${s.price.toFixed(2)}`,
    line2: line2Parts.join(' · '),
    glyph: status === 'win' ? '✓' : status === 'stop' ? '✗' : '',
    isLong,
  };
}

/** Chip size in CSS px (same fonts and padding as _drawSignal). */
export function signalChipSize(
  line1Full: string,
  line2: string,
  measure: (text: string, font: string) => number,
): { w: number; h: number } {
  const w1 = measure(line1Full, '800 10px sans-serif');
  const w2 = line2 ? measure(line2, '600 8px sans-serif') : 0;
  return { w: Math.max(w1, w2) + 12, h: 6 + 10 + (line2 ? 2 + 8 : 0) };
}

/** Occupied chip bounds within one frame (bitmap px), for overlap stacking. */
interface ChipRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface FairPriceRendererData {
  anchors: AnchorRendererItem[];
  signals: SignalRendererItem[];
  guide: GuideRendererItem | null;
  /** 0..1 live-dot pulse intensity this frame (0 = settled steady glow). */
  livePulse: number;
}

// ============================================================================
// Renderer
// ============================================================================

class FairPriceRenderer implements IPrimitivePaneRenderer {
  /** Label geometry from the last frame, CSS pixels. Rebuilt on every draw. */
  labelRects: FairPriceLabelRect[] = [];

  private _data: FairPriceRendererData = {
    anchors: [],
    signals: [],
    guide: null,
    livePulse: 0,
  };

  update(data: FairPriceRendererData): void {
    this._data = data;
  }

  draw(target: any): void {
    // Cleared BEFORE the early return below, not inside the draw block. An
    // anchor that stops rendering must stop being hoverable in the same frame;
    // leaving last frame's rects behind would keep a tooltip alive for a label
    // that is no longer on screen.
    this.labelRects = [];
    if (
      this._data.anchors.length === 0 &&
      this._data.signals.length === 0 &&
      this._data.guide === null
    )
      return;
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      // Never let a bad frame take down the chart's render loop.
      try {
        this._drawFrame(
          ctx,
          scope.horizontalPixelRatio as number,
          scope.verticalPixelRatio as number
        );
      } catch {
        // drop the frame silently
      }
    });
  }

  private _drawFrame(ctx: CanvasRenderingContext2D, hRatio: number, vRatio: number): void {
    for (const anchor of this._data.anchors) {
      this._drawAnchor(ctx, anchor, hRatio, vRatio);
    }
    if (this._data.guide) {
      this._drawGuide(ctx, this._data.guide, hRatio, vRatio);
    }
    // Chips stack against each other within a frame: draw left-to-right and
    // track occupied rects so overlapping chips bump away from the candle.
    const placedChips: ChipRect[] = [];
    const orderedSignals = [...this._data.signals].sort((a, b) => a.x - b.x);
    for (const signal of orderedSignals) {
      this._drawSignal(ctx, signal, hRatio, vRatio, placedChips);
    }
  }

  // --- Anchors --------------------------------------------------------------

  private _drawAnchor(
    ctx: CanvasRenderingContext2D,
    item: AnchorRendererItem,
    hRatio: number,
    vRatio: number
  ): void {
    const startPx = item.startX * hRatio;
    const rightPx = item.rightX * hRatio;
    if (rightPx <= startPx) return;

    ctx.save();
    ctx.globalAlpha = item.emphasized ? 1.0 : item.alpha;
    ctx.shadowBlur = 0;

    // Active anchor strokes get a subtle glow + 2px solid stroke.
    const strokeWidth = item.emphasized
      ? Math.max(2, 2 * vRatio)
      : Math.max(1, vRatio);
    const applyGlow = (): void => {
      if (item.emphasized) {
        ctx.shadowBlur = ACTIVE_GLOW_BLUR * hRatio;
        ctx.shadowColor = item.color;
      }
    };
    const resetGlow = (): void => {
      ctx.shadowBlur = 0;
    };

    if (item.band) {
      const topPy = item.yTop * vRatio;
      const bottomPy = item.yBottom * vRatio;
      const y = Math.min(topPy, bottomPy);
      const h = Math.max(1, Math.abs(bottomPy - topPy));

      // Translucent fill
      ctx.fillStyle = hexToRgba(item.color, BAND_FILL_ALPHA);
      ctx.fillRect(startPx, y, rightPx - startPx, h);

      // Top/bottom borders (2px solid + glow when emphasized, 1px otherwise)
      ctx.strokeStyle = item.emphasized
        ? item.color
        : hexToRgba(item.color, BAND_BORDER_ALPHA);
      ctx.lineWidth = strokeWidth;
      ctx.setLineDash([]);
      applyGlow();
      ctx.beginPath();
      ctx.moveTo(startPx, y);
      ctx.lineTo(rightPx, y);
      ctx.moveTo(startPx, y + h);
      ctx.lineTo(rightPx, y + h);
      ctx.stroke();
      resetGlow();
    } else {
      // Open line at the reference price: dashed 1px normally, solid 2px +
      // glow when this is the active anchor.
      const yPy = item.yPrice * vRatio;
      ctx.strokeStyle = item.emphasized ? item.color : hexToRgba(item.color, 0.9);
      ctx.lineWidth = strokeWidth;
      ctx.setLineDash(item.emphasized ? [] : [4 * hRatio, 4 * hRatio]);
      applyGlow();
      ctx.beginPath();
      ctx.moveTo(startPx, yPy);
      ctx.lineTo(rightPx, yPy);
      ctx.stroke();
      resetGlow();
      ctx.setLineDash([]);
    }

    if (item.emphasized) {
      // Price label chip at the right edge, centered on the anchor price,
      // e.g. "8PM 28830.00" — filled with the anchor color for visibility.
      const text = `${item.label} ${item.priceText}`;
      const fontSize = Math.round(9 * hRatio);
      const padX = 4 * hRatio;
      const padY = 3 * vRatio;
      ctx.font = `700 ${fontSize}px sans-serif`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'right';
      const textWidth = ctx.measureText(text).width;
      const chipRight = rightPx - 2 * hRatio;
      const chipLeft = chipRight - textWidth - padX * 2;
      const chipHeight = fontSize + padY * 2;
      const chipTop = item.yPrice * vRatio - chipHeight / 2;
      this._roundRectPath(ctx, chipLeft, chipTop, chipRight - chipLeft, chipHeight, 3 * hRatio);
      ctx.fillStyle = item.color;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.45)';
      ctx.lineWidth = Math.max(1, vRatio);
      ctx.stroke();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
      ctx.fillText(text, chipRight - padX, chipTop + chipHeight / 2);
      // Back to CSS pixels: everything above is bitmap space, but the crosshair
      // reports CSS coordinates.
      this.labelRects.push({
        kind: item.kind,
        left: chipLeft / hRatio,
        top: chipTop / vRatio,
        right: chipRight / hRatio,
        bottom: (chipTop + chipHeight) / vRatio,
      });
    } else {
      // Small label at the right edge, pill background so it stays readable.
      //
      // Drawn at FULL opacity even when the anchor is inactive. The line itself
      // stays dimmed - it is context and a bright line everywhere makes the
      // chart unreadable - but the label is identification. At INACTIVE_ALPHA
      // the text AND its backing plate both faded (the plate landed at 24%
      // black), which left GLBX/ASIA/PM washed out over candles and effectively
      // unreadable.
      ctx.globalAlpha = 1;
      const fontSize = Math.round(10 * hRatio);
      const pad = 4 * hRatio;
      ctx.font = `700 ${fontSize}px sans-serif`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'right';
      const labelY = item.band
        ? Math.min(item.yTop, item.yBottom) * vRatio - fontSize / 2 - pad
        : item.yPrice * vRatio - fontSize / 2 - pad;
      const labelX = rightPx - 4 * hRatio;
      const textWidth = ctx.measureText(item.label).width;
      const boxX = labelX - textWidth - pad;
      const boxY = labelY - fontSize / 2 - pad;
      const boxW = textWidth + pad * 2;
      const boxH = fontSize + pad * 2;
      // Near-opaque plate so the label never has to compete with a candle
      // behind it, plus a hairline in the anchor's own colour to tie the label
      // to its line without brightening the line itself.
      ctx.fillStyle = 'rgba(10, 14, 24, 0.92)';
      this._roundRectPath(ctx, boxX, boxY, boxW, boxH, 3 * hRatio);
      ctx.fill();
      ctx.strokeStyle = hexToRgba(item.color, 0.55);
      ctx.lineWidth = Math.max(1, vRatio);
      ctx.stroke();
      ctx.fillStyle = item.color;
      ctx.fillText(item.label, labelX, labelY);
      // Derived from the same box that was just painted, so the hover target
      // cannot drift from the visible plate.
      this.labelRects.push({
        kind: item.kind,
        left: boxX / hRatio,
        top: boxY / vRatio,
        right: (boxX + boxW) / hRatio,
        bottom: (boxY + boxH) / vRatio,
      });
    }

    ctx.restore();
  }

  // --- Distance guide -------------------------------------------------------

  /**
   * Thin dotted vertical connector from the latest bar's close to the active
   * anchor price, with the point distance rendered midway.
   */
  private _drawGuide(
    ctx: CanvasRenderingContext2D,
    item: GuideRendererItem,
    hRatio: number,
    vRatio: number
  ): void {
    // Offset in logical px before scaling, so the visual gap stays constant
    // rather than growing on high-DPI panes.
    const xPx = (item.x + GUIDE_X_OFFSET_PX) * hRatio;
    const yClosePy = item.yClose * vRatio;
    const yAnchorPy = item.yAnchor * vRatio;

    ctx.save();
    ctx.shadowBlur = 0;

    // Dotted 1px vertical connector.
    ctx.strokeStyle = hexToRgba(item.color, 0.75);
    ctx.lineWidth = Math.max(1, hRatio);
    ctx.setLineDash([2 * vRatio, 3 * vRatio]);
    ctx.beginPath();
    ctx.moveTo(xPx, yClosePy);
    ctx.lineTo(xPx, yAnchorPy);
    ctx.stroke();
    ctx.setLineDash([]);

    // Distance text midway, on a small dark pill.
    const fontSize = Math.round(9 * hRatio);
    const pad = 3 * hRatio;
    ctx.font = `700 ${fontSize}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const midY = (yClosePy + yAnchorPy) / 2;
    const textWidth = ctx.measureText(item.text).width;
    this._roundRectPath(
      ctx,
      xPx - textWidth / 2 - pad,
      midY - fontSize / 2 - pad,
      textWidth + pad * 2,
      fontSize + pad * 2,
      2 * hRatio
    );
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fill();
    ctx.fillStyle = item.color;
    ctx.fillText(item.text, xPx, midY);

    ctx.restore();
  }

  // --- Signal badges --------------------------------------------------------

  /**
   * One signal = triangle touching the candle edge + 1px stem + two-line info
   * chip + tiny dot at the exact entry price. `placed` accumulates chip rects
   * for this frame; a chip that would overlap an earlier one bumps away from
   * the candle by chip-height + gap until it fits.
   */
  private _drawSignal(
    ctx: CanvasRenderingContext2D,
    item: SignalRendererItem,
    hRatio: number,
    vRatio: number,
    placed: ChipRect[]
  ): void {
    const xPx = item.x * hRatio;
    const yEntryPy = item.yEntry * vRatio;
    const yEdgePy = item.yCandleEdge * vRatio;

    const line1Font = Math.round(10 * hRatio);
    const line2Font = Math.round(8 * hRatio);
    const padX = 6 * hRatio;
    const padY = 3 * vRatio;
    const lineGap = 2 * vRatio;

    ctx.save();
    ctx.shadowBlur = 0;
    ctx.setLineDash([]);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Lifecycle dimming applies to the whole badge (triangle/stem/dot/chip).
    ctx.globalAlpha = item.alpha;

    // Measure both lines to size the chip (line 1 includes the status glyph).
    ctx.font = `800 ${line1Font}px sans-serif`;
    const line1Full = item.glyph ? `${item.glyph} ${item.line1}` : item.line1;
    const w1 = ctx.measureText(line1Full).width;
    ctx.font = `600 ${line2Font}px sans-serif`;
    const w2 = item.line2 ? ctx.measureText(item.line2).width : 0;
    const shared = item.chipBox !== undefined;
    const chipWidth = Math.max(w1, w2) + padX * 2;
    const chipHeight = padY * 2 + line1Font + (item.line2 ? lineGap + line2Font : 0);
    const chipLeft = item.chipBox ? (item.chipBox.left + item.chipBox.w / 2) * hRatio - chipWidth / 2 : xPx - chipWidth / 2;

    // Base position: offset from the candle edge (below for longs, above for
    // shorts), then bump away from the candle while overlapping placed chips.
    const offset = SIGNAL_CHIP_OFFSET_PX * vRatio;
    const stackStep = chipHeight + SIGNAL_STACK_GAP_PX * vRatio;
    let chipTop = item.chipBox
      ? (item.chipBox.top + item.chipBox.h / 2) * vRatio - chipHeight / 2
      : item.below ? yEdgePy + offset : yEdgePy - offset - chipHeight;
    const overlaps = (top: number): boolean =>
      placed.some(
        (r) =>
          chipLeft < r.right &&
          chipLeft + chipWidth > r.left &&
          top < r.bottom &&
          top + chipHeight > r.top
      );
    let bumps = 0;
    while (!shared && overlaps(chipTop) && bumps < 50) {
      chipTop += item.below ? stackStep : -stackStep;
      bumps += 1;
    }
    placed.push({
      left: chipLeft,
      top: chipTop,
      right: chipLeft + chipWidth,
      bottom: chipTop + chipHeight,
    });

    // Triangle touching the candle: apex at the low pointing up (long) /
    // apex at the high pointing down (short).
    // In the shared layout the triangle sits where its box landed (the candle
    // edge unless something nearer the candle took it), and a folded part draws nothing.
    const apexPy = item.triApex != null ? item.triApex * vRatio : yEdgePy;
    const triW = SIGNAL_TRIANGLE_W_PX * hRatio;
    const triH = SIGNAL_TRIANGLE_H_PX * vRatio;
    const triBaseY = item.below ? apexPy + triH : apexPy - triH;
    const drawTri = item.triApex !== null;
    const drawChip = item.chipBox !== null;
    if (drawTri) {
      ctx.fillStyle = item.color;
      ctx.beginPath();
      ctx.moveTo(xPx, apexPy);
      ctx.lineTo(xPx - triW / 2, triBaseY);
      ctx.lineTo(xPx + triW / 2, triBaseY);
      ctx.closePath();
      ctx.fill();
    }

    // 1px stem from the triangle base to the chip edge (ties chip -> bar).
    if (drawTri && drawChip && !shared) {
      const stemEnd = item.below ? chipTop : chipTop + chipHeight;
      ctx.strokeStyle = hexToRgba(item.color, 0.65);
      ctx.lineWidth = Math.max(1, hRatio);
      ctx.beginPath();
      ctx.moveTo(xPx, triBaseY);
      ctx.lineTo(xPx, stemEnd);
      ctx.stroke();
    }

    // Tiny dot at the exact entry price on the signal bar's x. Not in the
    // shared layout: nothing paints on the candle, the leader marks the price.
    if (!shared) {
      ctx.beginPath();
      ctx.arc(xPx, yEntryPy, 2 * hRatio, 0, Math.PI * 2);
      ctx.fillStyle = item.color;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.lineWidth = Math.max(1, hRatio);
      ctx.stroke();
    }

    if (!drawChip) {
      ctx.restore();
      return;
    }

    // Chip: dark translucent fill (candles stay visible) + status border
    // (direction color while live, green/red/grey once resolved).
    this._roundRectPath(ctx, chipLeft, chipTop, chipWidth, chipHeight, 3 * hRatio);
    ctx.fillStyle = 'rgba(10, 14, 22, 0.82)';
    ctx.fill();
    ctx.strokeStyle = item.borderColor;
    ctx.lineWidth = Math.max(1, hRatio);
    ctx.stroke();

    // Line 1: [status glyph] grade + arrow + entry (bold, direction color).
    // The glyph gets its own color (green ✓ / red ✗) so the two are drawn
    // left-aligned back to back, centered as a whole.
    ctx.font = `800 ${line1Font}px sans-serif`;
    const line1Y = chipTop + padY + line1Font / 2;
    if (item.glyph) {
      ctx.textAlign = 'left';
      const startX = xPx - w1 / 2;
      const glyphText = `${item.glyph} `;
      ctx.fillStyle = item.glyphColor;
      ctx.fillText(glyphText, startX, line1Y);
      ctx.fillStyle = item.color;
      ctx.fillText(item.line1, startX + ctx.measureText(glyphText).width, line1Y);
      ctx.textAlign = 'center';
    } else {
      ctx.fillStyle = item.color;
      ctx.fillText(item.line1, xPx, line1Y);
    }

    // Line 2: bracket · anchor · distance (smaller, muted white).
    if (item.line2) {
      ctx.font = `600 ${line2Font}px sans-serif`;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.fillText(
        item.line2,
        xPx,
        chipTop + padY + line1Font + lineGap + line2Font / 2
      );
    }

    // Live indicator: small bright dot in the chip's top-right corner with a
    // steady glow; a fresh live signal briefly boosts glow + radius via the
    // finite pulse (this._data.livePulse eases 1 -> 0, then the loop stops).
    if (item.liveDot) {
      const pulse = this._data.livePulse;
      const r = (LIVE_DOT_RADIUS_PX + pulse * LIVE_DOT_PULSE_EXTRA_RADIUS_PX) * hRatio;
      const cx = chipLeft + chipWidth - (LIVE_DOT_RADIUS_PX + 2) * hRatio;
      const cy = chipTop + (LIVE_DOT_RADIUS_PX + 2) * vRatio;
      ctx.shadowBlur =
        (LIVE_DOT_GLOW_BASE_PX + pulse * LIVE_DOT_GLOW_PEAK_EXTRA_PX) * hRatio;
      ctx.shadowColor = LIVE_DOT_COLOR;
      ctx.fillStyle = LIVE_DOT_COLOR;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    ctx.restore();
  }

  /** Manual rounded-rect path (ctx.roundRect isn't in all targets/typings). */
  private _roundRectPath(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
  ): void {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.arcTo(x + w, y, x + w, y + radius, radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
    ctx.lineTo(x + radius, y + h);
    ctx.arcTo(x, y + h, x, y + h - radius, radius);
    ctx.lineTo(x, y + radius);
    ctx.arcTo(x, y, x + radius, y, radius);
    ctx.closePath();
  }
}

// ============================================================================
// Pane View
// ============================================================================

class FairPricePaneView implements IPrimitivePaneView {
  private _source: FairPricePrimitive;
  private _renderer: FairPriceRenderer;

  constructor(source: FairPricePrimitive) {
    this._source = source;
    this._renderer = new FairPriceRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    // Below the top-most labels but above the grid; 'normal' keeps the
    // translucent bands from washing out candle wicks less than 'top' would.
    return 'normal';
  }

  renderer(): IPrimitivePaneRenderer {
    this._renderer.update(this._source.getRendererData());
    return this._renderer;
  }

  /** Label geometry from the last painted frame. */
  labelRects(): readonly FairPriceLabelRect[] {
    return this._renderer.labelRects;
  }
}

// ============================================================================
// Primitive
// ============================================================================

export class FairPricePrimitive implements ISeriesPrimitive<Time> {
  private _data: FairPriceData = {
    anchors: [],
    signals: [],
    activeAnchorKind: null,
    armed: false,
  };
  private _paneView: FairPricePaneView;
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];

  // --- Live-dot pulse state (finite rAF loop, BosLabelPrimitive pattern) ---
  /** Live signal keys seen on the previous update; null until the first update
   *  so the initial (historical) load never flashes. */
  private _knownLiveKeys: Set<string> | null = null;
  private _pulseStart = 0; // performance.now() when the current pulse began
  private _pulseActive = false; // whether the finite pulse rAF loop is running
  private _rafId?: number; // handle to cancel the pulse loop on detach
  private _layout = new MarkerLayoutClient('fairPrice', (ctx) => this._layoutItems(ctx));

  /**
   * Anchor kind whose label is under the cursor, or null.
   *
   * Named `labelAt` to match `markerAt` on the trade-marker and candidate
   * primitives — same contract: CSS pixels in, id out, null for empty space.
   */
  labelAt(x: number, y: number): FairPriceAnchorItem['kind'] | null {
    return hitTestLabels(this._paneView.labelRects(), x, y)?.kind ?? null;
  }

  /** Anchor label boxes from the last painted frame (CSS px, pane-relative). */
  labelRects(): readonly FairPriceLabelRect[] {
    return this._paneView.labelRects();
  }

  constructor() {
    this._paneView = new FairPricePaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._layout.setAttached(true);
  }

  /** Join the chart's shared marker layout (null: chips stack among themselves). */
  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  /**
   * Two boxes per signal badge: the triangle touching the candle (closest of
   * all) and the info chip further out. The entry dot sits on the price itself
   * and the anchors are full-width bands / lines, so neither takes part.
   */
  private _layoutItems(ctx: LayoutContext): LayoutItem[] {
    const out: LayoutItem[] = [];
    this._data.signals.forEach((s, i) => {
      if (s.time < ctx.fromTime || s.time > ctx.toTime) return;
      const x = ctx.timeToX(s.time);
      if (x === null) return;
      const { line1, line2, glyph, isLong } = signalLines(s);
      const bar = ctx.bar(s.time);
      const edge = ctx.priceToY(isLong ? bar?.low ?? s.price : bar?.high ?? s.price);
      if (edge === null) return;
      const side = isLong ? 'below' : 'above';
      const id = s.id ?? `${s.time}:${s.direction}:${i}`;
      out.push({
        key: `tri:${id}`,
        time: s.time,
        x,
        side,
        anchorY: edge,
        w: SIGNAL_TRIANGLE_W_PX + 1,
        h: SIGNAL_TRIANGLE_H_PX + 1,
        gap: 0,
        priority: LAYOUT_PRIORITY.fairPriceTriangle,
        color: isLong ? SIGNAL_LONG_COLOR : SIGNAL_SHORT_COLOR,
        label: `fair price ${line1}`,
        ref: s,
      });
      const size = signalChipSize(glyph ? `${glyph} ${line1}` : line1, line2, ctx.measure);
      out.push({
        key: `chip:${id}`,
        time: s.time,
        x,
        side,
        anchorY: edge,
        w: Math.ceil(size.w) + 2,
        h: Math.ceil(size.h) + 2,
        gap: 8,
        // Shared layout: the leader from the exact entry price replaces the stem and the on-candle dot.
        leaderY: ctx.priceToY(s.price) ?? edge,
        color: isLong ? SIGNAL_LONG_COLOR : SIGNAL_SHORT_COLOR,
        priority: LAYOUT_PRIORITY.fairPriceChip,
        label: `fair price ${line1}${line2 ? ` · ${line2}` : ''}`,
        ref: s,
      });
    });
    return out;
  }

  detached(): void {
    this._layout.setAttached(false);
    if (this._rafId !== undefined) {
      cancelAnimationFrame(this._rafId);
      this._rafId = undefined;
    }
    this._pulseActive = false;
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {
    // Views pull fresh data via getRendererData() on each renderer() call.
  }

  /** Replace the full anchor/signal set and request a redraw. Pass empty arrays to clear. */
  update(data: FairPriceData): void {
    this._data = {
      anchors: data.anchors ?? [],
      signals: data.signals ?? [],
      activeAnchorKind: data.activeAnchorKind ?? null,
      armed: data.armed ?? false,
    };
    this._trackLiveSignals(this._data.signals);
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  /**
   * Detect newly-arrived live signals and kick a short finite pulse on the
   * live dot. The first update after construction only seeds the seen set
   * (historical signals loaded at once must not flash).
   */
  private _trackLiveSignals(signals: FairPriceSignalItem[]): void {
    const liveKeys = new Set<string>();
    for (const s of signals) {
      if ((s.status ?? 'live') === 'live') {
        liveKeys.add(s.id ?? `${s.time}:${s.direction}`);
      }
    }
    if (this._knownLiveKeys !== null) {
      for (const key of liveKeys) {
        if (!this._knownLiveKeys.has(key)) {
          this._startLivePulse();
          break;
        }
      }
    }
    this._knownLiveKeys = liveKeys;
  }

  /**
   * Kick a short, FINITE pulse: re-request redraws via requestAnimationFrame
   * for LIVE_PULSE_DURATION_MS so the live dot flashes bright then settles,
   * then the loop stops (no perpetual animation). A pulse already running just
   * restarts its clock instead of stacking rAF loops.
   */
  private _startLivePulse(): void {
    this._pulseStart = performance.now();
    if (this._pulseActive) return; // existing loop picks up the new start time
    this._pulseActive = true;
    const tick = (): void => {
      const elapsed = performance.now() - this._pulseStart;
      this._requestUpdate?.();
      if (elapsed < LIVE_PULSE_DURATION_MS && this._requestUpdate) {
        this._rafId = requestAnimationFrame(tick);
      } else {
        this._pulseActive = false;
        this._rafId = undefined;
      }
    };
    this._rafId = requestAnimationFrame(tick);
  }

  /** Current pulse intensity: 1 (fresh live signal) easing out to 0 (settled). */
  private _livePulseIntensity(): number {
    if (!this._pulseActive) return 0;
    const elapsed = performance.now() - this._pulseStart;
    const t = Math.min(1, Math.max(0, elapsed / LIVE_PULSE_DURATION_MS));
    return (1 - t) * (1 - t); // ease-out
  }

  /**
   * Convert anchors/signals into logical-px renderer items using the chart's
   * time/price coordinate converters. Anchors extend from their set time to
   * the right edge of the pane; a set time older than the visible range clamps
   * to the left edge (x=0) instead of being dropped.
   */
  getRendererData(): FairPriceRendererData {
    const empty: FairPriceRendererData = {
      anchors: [],
      signals: [],
      guide: null,
      livePulse: 0,
    };
    if (!this._series || !this._chart) return empty;
    if (this._data.anchors.length === 0 && this._data.signals.length === 0) return empty;

    try {
      const timeScale = this._chart.timeScale();
      const paneWidth = timeScale.width();
      if (!paneWidth || paneWidth <= 0) return empty;
      const visibleRange = timeScale.getVisibleRange();

      const timeToX = (timeSecs: number): number | null => {
        const coord = timeScale.timeToCoordinate(timeSecs as Time);
        if (coord !== null) return coord;
        // Off the loaded/visible range: clamp to the left edge when the time
        // precedes it (bands set hours ago must still cover the pane).
        if (visibleRange !== null && timeSecs <= (visibleRange.from as number)) return 0;
        return null;
      };

      const anchors: AnchorRendererItem[] = [];
      for (const a of this._data.anchors) {
        const style = ANCHOR_STYLES[a.kind];
        if (!style) continue;
        const startX = timeToX(a.setAtTime);
        if (startX === null) continue;

        const yPrice = this._series.priceToCoordinate(a.price);
        if (yPrice === null) continue;

        let yTop = yPrice;
        let yBottom = yPrice;
        if (style.band) {
          const top = this._series.priceToCoordinate(a.priceHigh);
          const bottom = this._series.priceToCoordinate(a.priceLow);
          if (top === null || bottom === null) continue;
          yTop = top;
          yBottom = bottom;
        }

        anchors.push({
          kind: a.kind,
          band: style.band,
          startX,
          rightX: paneWidth,
          yTop,
          yBottom,
          yPrice,
          color: style.color,
          label: anchorLabel(a, style),
          alpha: a.active ? 1.0 : INACTIVE_ALPHA,
          emphasized: this._data.activeAnchorKind !== null && a.kind === this._data.activeAnchorKind,
          priceText: a.price.toFixed(2),
        });
      }

      // Distance guide: latest bar close -> active anchor price (armed only).
      let guide: GuideRendererItem | null = null;
      if (this._data.armed && this._data.activeAnchorKind !== null) {
        const activeAnchor = this._data.anchors.find(
          (a) => a.kind === this._data.activeAnchorKind
        );
        const style = activeAnchor ? ANCHOR_STYLES[activeAnchor.kind] : undefined;
        if (activeAnchor && style) {
          // Latest bar straight off the attached series (updates every frame
          // without needing a store round-trip).
          const seriesData = (this._series as { data?: () => readonly unknown[] }).data?.();
          const last =
            seriesData && seriesData.length > 0
              ? (seriesData[seriesData.length - 1] as {
                  time?: unknown;
                  close?: unknown;
                  value?: unknown;
                })
              : null;
          const close =
            last && typeof last.close === 'number'
              ? last.close
              : last && typeof last.value === 'number'
                ? last.value
                : null;
          if (last && close !== null && last.time != null) {
            const x = timeScale.timeToCoordinate(last.time as Time);
            const yClose = this._series.priceToCoordinate(close);
            const yAnchor = this._series.priceToCoordinate(activeAnchor.price);
            if (
              x !== null &&
              yClose !== null &&
              yAnchor !== null &&
              Math.abs(yClose - yAnchor) >= GUIDE_MIN_GAP_PX
            ) {
              guide = {
                x,
                yClose,
                yAnchor,
                text: `${Math.abs(close - activeAnchor.price).toFixed(1)}pt`,
                color: style.color,
              };
            }
          }
        }
      }

      // Bar high/low lookup so triangles touch the actual candle extremes.
      // Rebuilt per frame; a linear scan over the loaded bars is cheap
      // relative to the canvas work and avoids a stale cache on data reload.
      let barMap: Map<number, { high: number; low: number }> | null = null;
      if (this._data.signals.length > 0) {
        const seriesData =
          (this._series as { data?: () => readonly unknown[] }).data?.() ?? [];
        if (seriesData.length > 0) {
          barMap = new Map();
          for (const raw of seriesData) {
            const b = raw as { time?: unknown; high?: unknown; low?: unknown };
            if (
              typeof b.time === 'number' &&
              typeof b.high === 'number' &&
              typeof b.low === 'number'
            ) {
              barMap.set(b.time, { high: b.high, low: b.low });
            }
          }
        }
      }

      // Shared layout: where each badge's triangle and chip landed.
      const shared = this._layout.placed();
      const triAt = new Map<FairPriceSignalItem, number>();
      const chipAt = new Map<FairPriceSignalItem, LayoutBox>();
      if (shared) {
        for (const p of shared) {
          const sig = p.item.ref as FairPriceSignalItem;
          if (p.item.key.startsWith('tri:')) triAt.set(sig, sig.direction === 'long' ? p.top : p.top + p.h);
          else chipAt.set(sig, { left: p.left + 1, top: p.top + 1, w: p.w - 2, h: p.h - 2 });
        }
      }

      const signals: SignalRendererItem[] = [];
      for (const s of this._data.signals) {
        const x = timeScale.timeToCoordinate(s.time as Time);
        // Bar outside the visible range -> skip the whole badge.
        if (x === null || x < 0 || x > paneWidth) continue;
        const yEntry = this._series.priceToCoordinate(s.price);
        if (yEntry === null) continue;

        // Candle edge the triangle touches: low for longs, high for shorts.
        // Fall back to the entry price when the bar isn't in the series data.
        const bar = barMap?.get(s.time);
        const isLong = s.direction === 'long';
        const edgePrice = isLong ? bar?.low ?? s.price : bar?.high ?? s.price;
        const yCandleEdge = this._series.priceToCoordinate(edgePrice) ?? yEntry;

        const line2Parts = [s.riskText, SIGNAL_ANCHOR_LABELS[s.anchorKind]].filter(Boolean);
        if (typeof s.distance === 'number' && Number.isFinite(s.distance)) {
          line2Parts.push(`${Math.round(s.distance)}pt`);
        }

        // Lifecycle treatment (missing status = live, older engine payloads).
        const status: FairPriceSignalItemStatus = s.status ?? 'live';
        if (status === 'expired') line2Parts.push('exp');
        const color = isLong ? SIGNAL_LONG_COLOR : SIGNAL_SHORT_COLOR;

        signals.push({
          x,
          yEntry,
          yCandleEdge,
          below: isLong,
          line1: `${s.grade} ${isLong ? '▲' : '▼'} ${s.price.toFixed(2)}`,
          line2: line2Parts.join(' · '),
          color,
          alpha:
            status === 'live'
              ? 1
              : status === 'expired'
                ? EXPIRED_ALPHA
                : RESOLVED_ALPHA,
          borderColor:
            status === 'win'
              ? STATUS_WIN_COLOR
              : status === 'stop'
                ? STATUS_STOP_COLOR
                : status === 'expired'
                  ? STATUS_EXPIRED_COLOR
                  : color,
          glyph: status === 'win' ? '✓' : status === 'stop' ? '✗' : '',
          glyphColor: status === 'win' ? STATUS_WIN_COLOR : STATUS_STOP_COLOR,
          liveDot: status === 'live',
          ...(shared ? { chipBox: chipAt.get(s) ?? null, triApex: triAt.get(s) ?? null } : {}),
        });
      }

      return { anchors, signals, guide, livePulse: this._livePulseIntensity() };
    } catch {
      // Coordinate conversion during teardown/resize can transiently fail —
      // return an empty frame rather than throwing into the render loop.
      return empty;
    }
  }
}

// ============================================================================
// Factory Function
// ============================================================================

export function createFairPricePrimitive(): FairPricePrimitive {
  return new FairPricePrimitive();
}
